import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { getPost, POSTS } from "@/data/posts";
import { SITE } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;
  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    author: { "@type": "Organization", name: "MESCO Springs", url: SITE.mainSite },
    publisher: { "@type": "Organization", name: "MESCO Springs", url: SITE.mainSite },
  };

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      </Helmet>

      <div className="pt-44 pb-16 border-b border-[#334772]/15 blueprint-grid" data-testid="post-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#706F70]">
              <Link to="/journal" className="hover:text-[#334772] transition-colors">Journal</Link>
              <span className="mx-2 text-[#334772]">/</span>{post.tag}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-[#334772] mt-6">
              {post.title}
            </h1>
            <div className="mt-6 flex items-center gap-5 font-mono-spec text-[11px] tracking-[0.25em] uppercase text-[#9C9C9C]">
              <span>{post.date}</span>
              <span className="text-[#334772]">{post.readTime}</span>
              <span>MESCO Engineering Desk</span>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <Reveal>
          <p className="text-xl text-[#334772] leading-relaxed italic border-l-4 border-[#334772] pl-6">
            "{post.pullQuote}"
          </p>
        </Reveal>
        {post.sections.map((s, i) => (
          <Reveal key={s.heading} className="mt-14">
            <div className="flex items-center gap-4 mb-4">
              <span className="font-mono-spec text-[#334772] text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</span>
              <span className="h-px w-10 bg-[#334772]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-[#334772]">{s.heading}</h2>
            {s.body.map((p, j) => (
              <p key={j} className="mt-4 text-[#706F70] leading-relaxed">{p}</p>
            ))}
          </Reveal>
        ))}

        <Reveal className="mt-16">
          <div className="border border-[#334772]/40 bg-[#334772]/5 p-8 lg:p-10">
            <h3 className="font-display text-3xl uppercase tracking-wide text-[#334772]">Have a spring problem like this?</h3>
            <p className="mt-3 text-sm text-[#706F70] leading-relaxed">
              Send the drawing, the failed spring or the duty data. Our engineers respond within 24 hours.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link to="/enquiry" data-testid="post-rfq-btn" className="group inline-flex items-center gap-3 px-7 py-3.5 bg-[#334772] text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#2A3C60] transition-colors">
                Send an RFQ <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" data-testid="post-mainsite-btn" className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#334772]/40 text-[#334772] text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#334772] hover:text-white transition-colors">
                mescosprings.com <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="border-t border-[#334772]/15 bg-[#F3F5F8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <Reveal>
            <h2 className="font-display text-3xl uppercase tracking-wide text-[#334772] mb-8">More from the journal</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <Link to={`/journal/${r.slug}`} data-testid={`related-post-${r.slug}`} className="group block border border-[#334772]/15 p-6 hover:border-[#334772] hover:-translate-y-1 transition-all duration-300 bg-[#F3F5F8]">
                  <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#334772]">{r.tag}</span>
                  <h3 className="font-display text-2xl text-[#334772] uppercase tracking-wide leading-tight mt-2 group-hover:text-[#706F70] transition-colors">{r.title}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
